import { FC, JSX, useMemo } from "react";
import { isNullOrUndefined } from "@app/shared/utility/isNullOrUndefined";
import { InfoBadge } from "@app/shared/InfoBadge";
import { QesColors } from "@app/shared/qes-standards/colors";
import { ZipCoverageMapDetails, ZipCoverageMapSelections, ZipProviderFilters } from "@app/projects/zip-projects/maps/interfaces";
import { providerSourceFiltersComparer } from "@app/projects/zip-projects/maps/zipProviderFilters";
import {
  ConvertProviderSourceFilterOptionsToDisplay,
} from "@app/projects/network-assessment/maps/network-coverage-map/DisplayConverters";
import {
  HUNDRED_PERCENT_FILTER,
  NOT_AVAILABLE_PLACEHOLDER,
  ZERO_PERCENT_FILTER,
} from "@app/projects/zip-projects/maps/ZipCoverageMapConstants";
import ZipCoverageMapZipDetailInfo from "@app/projects/zip-projects/maps/ZipCoverageMapZipDetailInfo";
import { truncateStringWithEllipsis } from "@app/shared/utility/truncateStringWithEllipsis";
import TooltipWrapper from "@app/shared/TooltipWrapper";

const MAX_MEMBERSHIP_CHIP_LENGTH = 20;

interface ZipCoverageMapDetailsBoxProps {
  /** null renders the idle (pre-Create) state. */
  details: ZipCoverageMapDetails | null;
  appliedSelections: ZipCoverageMapSelections | null;
  appliedProviderFilters: ZipProviderFilters;
  membershipLabel: string | null;
  showAverageDistance: boolean;
  showMaximumDistance: boolean;
  showAverageTime: boolean;
  showMaximumTime: boolean;
}

const formatCount = (value: number | null): string =>
  isNullOrUndefined(value) ? NOT_AVAILABLE_PLACEHOLDER : (value as number).toLocaleString();

const formatDecimal = (value: number | null): string =>
  isNullOrUndefined(value) ? NOT_AVAILABLE_PLACEHOLDER : (value as number).toFixed(1);

const buildFilterBadge = (content: string, idSuffix: string): JSX.Element => (
  <InfoBadge
    key={idSuffix}
    content={content}
    testId={`qes-zip-coverage-map-details__applied-filter-${idSuffix}`}
    icon={null}
  />
);

const buildMembershipChip = (label: string): JSX.Element => {
  const shouldTruncate = label.length > MAX_MEMBERSHIP_CHIP_LENGTH;
  const badge = (
    <InfoBadge
      testId="qes-zip-coverage-map-details__membership"
      className="qes-zip-coverage-map-details__membership"
      content={shouldTruncate ? truncateStringWithEllipsis(label, MAX_MEMBERSHIP_CHIP_LENGTH) : label}
      icon={null}
    />
  );

  return shouldTruncate ? (
    <TooltipWrapper
      position="top"
      text={label}
      tooltipBodyClassName="qes-zip-coverage-map-details__membership-tooltip"
    >
      {badge}
    </TooltipWrapper>
  ) : badge;
};

// Thin copy of county's MapDetailsBox, adapted for zip. Prop-driven / no context (county reads from its map context).
export const ZipCoverageMapDetailsBox: FC<ZipCoverageMapDetailsBoxProps> = ({
  details,
  appliedSelections,
  appliedProviderFilters,
  membershipLabel,
  showAverageDistance,
  showMaximumDistance,
  showAverageTime,
  showMaximumTime,
}) => {
  // Pills reflect what is actually filtering the map: Provider Source beyond the Network default, and the
  // Serves/Gains/Impact measures when non-default. A default Network-only selection leaves the row empty.
  // Extend as the Filter button gains Provider Claims Insights, matching county.
  const appliedFilters = useMemo<JSX.Element[]>(() => {
    if (!appliedSelections) {
      return [];
    }

    const badges: JSX.Element[] = [];

    [...appliedProviderFilters.providerFilterOptions]
      .sort(providerSourceFiltersComparer)
      .forEach((option) =>
        badges.push(buildFilterBadge(ConvertProviderSourceFilterOptionsToDisplay[option] ?? option, `source-${option}`)),
      );

    const [servesMin, servesMax] = appliedProviderFilters.servesRange;
    if (servesMin !== ZERO_PERCENT_FILTER || servesMax !== HUNDRED_PERCENT_FILTER) {
      badges.push(buildFilterBadge(`Serves %: ${servesMin}% - ${servesMax}%`, "serves"));
    }

    const [gainsMin, gainsMax] = appliedProviderFilters.gainsRange;
    if (gainsMin !== ZERO_PERCENT_FILTER || gainsMax !== HUNDRED_PERCENT_FILTER) {
      badges.push(buildFilterBadge(`Gains %: ${gainsMin}% - ${gainsMax}%`, "gains"));
    }

    const [impactMin, impactMax] = appliedProviderFilters.impactRange;
    if (impactMin !== ZERO_PERCENT_FILTER || impactMax !== HUNDRED_PERCENT_FILTER) {
      badges.push(buildFilterBadge(`Impact %: ${impactMin}% - ${impactMax}%`, "impact"));
    }

    return badges;
  }, [appliedSelections, appliedProviderFilters]);

  if (!details) {
    return (
      <div
        className="qes-zip-coverage-map-details qes-zip-coverage-map-details--idle"
        data-testid="qes-zip-coverage-map-details--idle"
      >
        Configure the inputs and select <strong>Create Map</strong> to see network coverage details for a zip.
      </div>
    );
  }

  // 5 fixed tiles (2 provider + 3 member) plus each enabled distance/time tile — county's divisor.
  const detailsItemMaxWidthPercent =
    (1 /
      (5 +
        Number(showAverageDistance) +
        Number(showMaximumDistance) +
        Number(showAverageTime) +
        Number(showMaximumTime))) *
    100;
  const tileStyle = { maxWidth: `${detailsItemMaxWidthPercent}%` };

  const membersWithAccessPercent = formatDecimal(details.membersWithAccessPercent);
  const membersWithoutAccessPercent = formatDecimal(details.membersWithoutAccessPercent);

  return (
    <div className="qes-zip-coverage-map-details" data-testid="qes-zip-coverage-map-details">
      <div className="qes-zip-coverage-map-details__callout-box">
        <ZipCoverageMapZipDetailInfo
          zipCode={details.zipCode}
          zipName={details.zipName}
          zipClass={details.zipClass}
          primaryCounty={details.primaryCounty}
          state={details.state}
          specialtyGroupName={details.specialtyGroupName}
          entityType={details.entityType}
          passPercentage={details.passPercentage}
          minimumProviderCount={details.minimumProviderCount}
          minimumDistancePhrase={details.minimumDistancePhrase}
        />
        <div className="qes-zip-coverage-map-details__chip-row">
          {details.zipClass && (
            <InfoBadge
              testId="qes-zip-coverage-map-details__zip-class"
              className="qes-zip-coverage-map-details__zip-class"
              content={`${details.zipClass} Zip Code`}
              icon={null}
              borderColor={QesColors.Blue500}
              backgroundColor="#DCECFF"
              contentColor={QesColors.QesPrimaryGray8}
            />
          )}
          {membershipLabel && buildMembershipChip(membershipLabel)}
        </div>
      </div>

      <div className="qes-zip-coverage-map-details__metrics-container">
        <div className="qes-zip-coverage-map-details__metrics">
          <div className="qes-zip-coverage-map-details__tiles">
            <div className="qes-zip-coverage-map-details__tile" style={tileStyle}>
              <strong>{formatCount(details.servicingProviderCount)}</strong> Servicing Providers
            </div>
            <div className="qes-zip-coverage-map-details__tile" style={tileStyle}>
              <strong>{formatCount(details.inZipServicingProviderCount)}</strong> In-Zip Servicing Providers
            </div>
            <div className="qes-zip-coverage-map-details__tile" style={tileStyle}>
              <strong>{formatCount(details.totalMembers)}</strong> Total Members
            </div>
            <div className="qes-zip-coverage-map-details__tile" style={tileStyle}>
              <strong>{formatCount(details.membersWithAccess)} ({membersWithAccessPercent}%)</strong> Members with Access
            </div>
            <div className="qes-zip-coverage-map-details__tile" style={tileStyle}>
              <strong>{formatCount(details.membersWithoutAccess)} ({membersWithoutAccessPercent}%)</strong> Members without Access
            </div>
            {showAverageDistance && (
              <div className="qes-zip-coverage-map-details__tile" style={tileStyle}>
                <strong>{formatDecimal(details.averageDistance)} miles</strong> Average Distance
              </div>
            )}
            {showMaximumDistance && (
              <div className="qes-zip-coverage-map-details__tile" style={tileStyle}>
                <strong>{formatDecimal(details.maxDistance)} miles</strong> Max Distance
              </div>
            )}
            {showAverageTime && (
              <div className="qes-zip-coverage-map-details__tile" style={tileStyle}>
                <strong>{formatDecimal(details.averageTime)} minutes</strong> Average Time
              </div>
            )}
            {showMaximumTime && (
              <div className="qes-zip-coverage-map-details__tile" style={tileStyle}>
                <strong>{formatDecimal(details.maxTime)} minutes</strong> Max Time
              </div>
            )}
          </div>
        </div>

        <div className="qes-zip-coverage-map-details__applied-filters-container">
          <div className="qes-zip-coverage-map-details__applied-filters">
            <h4 className="header-s">{appliedFilters.length > 0 ? "Applied Filters:" : "No Filters Applied"}</h4>
            {appliedFilters}
          </div>
          <p className="ms-1 mb-0">
            <i>
              Applied filters update the provider points shown on the map. Member points and map metrics remain unchanged by provider filter selection
            </i>
          </p>
        </div>
      </div>
    </div>
  );
};
